import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { collection, doc } from "firebase/firestore";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";
import { db } from "@/lib/firebase";
import { getOne, createItem, updateItem } from "@/lib/adminRepository";
import RichTextEditor from "@/components/admin/RichTextEditor";
import ImageUploader from "@/components/admin/ImageUploader";
import { ADMIN } from "@/constants/testIds";

const BLANK = {
  headline: "", metric: "", propertyType: "", sub: "", story: "",
  image: "", images: [], order: 99, status: "draft",
};

const Label = ({ children }) => <span className="editorial-label">{children}</span>;
const Hint = ({ children }) => <span className="block text-[11px] text-olive/80 mb-1">{children}</span>;

export default function ProvenResultsForm() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();

  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  // Stable doc id so cover/gallery upload paths are valid before the first save.
  const docId = useMemo(() => (isNew ? doc(collection(db, "provenResults")).id : id), [isNew, id]);
  const storagePath = `media/provenResults/${docId}`;

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getOne("provenResults", id);
        if (cancelled) return;
        if (data) setForm({ ...BLANK, ...data, images: Array.isArray(data.images) ? data.images : [] });
        else toast.error("Case study not found.");
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[provenResult] load failed:", err);
        toast.error("Failed to load case study.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isNew, id]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v && v.target ? v.target.value : v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.headline.trim()) return toast.error("Headline is required.");
    setSaving(true);
    try {
      const { id: _id, ...rest } = form;
      const payload = { ...rest, order: Number(form.order) || 99 };
      if (isNew) {
        await createItem("provenResults", payload, docId);
        toast.success("Case study created.");
      } else {
        await updateItem("provenResults", id, payload);
        toast.success("Case study saved.");
      }
      navigate("/admin/proven-results");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[provenResult] save failed:", err);
      toast.error("Save failed: " + (err?.message || "error"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-olive">Loading…</p>;

  return (
    <div data-testid="admin-proven-result-form">
      <Link to="/admin/proven-results" className="inline-flex items-center gap-2 text-[0.7rem] tracking-[0.22em] uppercase text-bronze hover:text-bronze_dark mb-5">
        <ArrowLeft size={12} /> Back to proven results
      </Link>
      <header className="mb-7">
        <div className="eyebrow text-bronze mb-1">{isNew ? "New" : "Edit"}</div>
        <h1 className="font-serif text-3xl md:text-4xl text-ink">{isNew ? "New Case Study" : form.headline || "Edit case study"}</h1>
      </header>

      <form onSubmit={handleSubmit} className="grid lg:grid-cols-[1fr,300px] gap-6">
        {/* Main */}
        <div className="space-y-6">
          <section className="border border-bronze/20 bg-cream p-6 space-y-5">
            <label className="block">
              <Label>Headline *</Label>
              <input className="editorial-input" value={form.headline} onChange={set("headline")} data-testid="case-headline" placeholder="e.g. Sold 18% above valuation in 3 weeks" />
            </label>
            <div className="grid sm:grid-cols-2 gap-5">
              <label className="block">
                <Label>Metric</Label>
                <Hint>Big stat over the image, e.g. “+S$220k”.</Hint>
                <input className="editorial-input" value={form.metric} onChange={set("metric")} data-testid="case-metric" placeholder="+S$220k" />
              </label>
              <label className="block">
                <Label>Property type</Label>
                <Hint>e.g. “HDB resale → Private condo”.</Hint>
                <input className="editorial-input" value={form.propertyType} onChange={set("propertyType")} data-testid="case-type" placeholder="HDB resale → Private condo" />
              </label>
            </div>
            <label className="block">
              <Label>Sub-label</Label>
              <Hint>Optional small eyebrow shown on the card.</Hint>
              <input className="editorial-input" value={form.sub} onChange={set("sub")} data-testid="case-sub" placeholder="Punggol · 5-room" />
            </label>
            <div className="block">
              <Label>Story</Label>
              <Hint>The full case study — challenge, strategy, outcome. Use the toolbar for headings, bold, lists, links, images.</Hint>
              <RichTextEditor value={form.story} onChange={set("story")} placeholder="Tell the story — what the family needed, what Kiap did, and the result…" />
            </div>
          </section>

          <section className="border border-bronze/20 bg-cream p-6">
            <div className="eyebrow text-bronze mb-1">Media</div>
            <h2 className="font-serif text-lg text-ink mb-3">Cover image</h2>
            <ImageUploader path={storagePath} value={form.image ? [form.image] : []} onChange={(urls) => set("image")(urls[0] || "")} single testid="case-cover" />
            <h2 className="font-serif text-lg text-ink mt-6 mb-3">Gallery</h2>
            <ImageUploader path={storagePath} value={form.images} onChange={set("images")} max={8} testid="case-gallery" />
            <p className="text-[11px] text-olive mt-3">The cover leads the card &amp; the detail hero; gallery images show further down the case study.</p>
          </section>
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <section className="border border-bronze/20 bg-cream p-6 space-y-5">
            <label className="block">
              <Label>Visibility</Label>
              <select className="editorial-input" value={form.status} onChange={set("status")} data-testid="case-status">
                <option value="published">published</option>
                <option value="draft">draft</option>
              </select>
            </label>
            <label className="block">
              <Label>Sort order</Label>
              <Hint>Lower numbers appear first.</Hint>
              <input type="number" className="editorial-input" value={form.order} onChange={set("order")} data-testid="case-order" />
            </label>
          </section>

          <div className="flex gap-3">
            <Link to="/admin/proven-results" className="btn-secondary flex-1 text-center">Cancel</Link>
            <button type="submit" disabled={saving} data-testid={ADMIN.saveButton} className="btn-primary flex-1 justify-center disabled:opacity-60">
              <Save size={14} /> {saving ? "Saving…" : isNew ? "Create" : "Save"}
            </button>
          </div>
        </aside>
      </form>
    </div>
  );
}
